import { FC, useEffect, useState } from 'react';
import { NumberUtils, Tr } from '@utils';
import { useLoggedinUser } from '@hooks';
import { AdditionalService, PackageDeal, Permission } from '@services';
import { Grid, IconButton, Stack, Switch, Typography } from '@mui/material';
import { EntityList, EntityListItem, EntityListItemCell, IEntityListProps } from '@components/common';
import { PackageDealServiceDetailDialog } from '../PackageDealServiceDetailDialog';
import { PackageDealIncludedServicesDialog } from '../PackageDealIncludedServicesDialog';
import { PackageDealAndServiceLogoViewer } from '../PackageDealAndServiceLogoViewer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

type Entity = PackageDeal | AdditionalService;
const MAX_SELECTABLE_PACKAGE_DEAL = 2;

export interface IPackageDealListViewerProps extends IEntityListProps<Entity> {
	entityType: 'service' | 'package';
	paddingX?: number | string;
	entities: Entity[];
	selected: Entity[];
	onSelectChange?: (selected: Entity[]) => void;
	onEdit?: (entity: Entity) => void;
	onDelete?: (entity: Entity) => void;
	onPackageDealSelectionChange?: (entity: PackageDeal, selected: boolean) => void;
}

export const PackageDealListViewer: FC<IPackageDealListViewerProps> = ({
	entityType,
	entities,
	selected,
	onSelectChange,
	onEdit,
	onDelete,
	onPackageDealSelectionChange,
	paddingX,
	...props
}) => {
	const user = useLoggedinUser();
	const [viewingService, setViewingService] = useState<AdditionalService>();
	const [viewingPackage, setViewingPackage] = useState<PackageDeal>();
	const hasWritePermission =
		user?.isAdmin() || user?.hasPermission(entityType === 'service' ? Permission.SERVICE_WRITE : Permission.PACKAGE_DEAL_WRITE);
	const handleSelectChange = (status: boolean, entity: Entity) => {
		const newSelections = selected.filter((s) => s.id !== entity.id);
		status && newSelections.push(entity);
		onSelectChange?.(newSelections);
	};
	const totalSelectionCount = entities.filter((entity) => (entity instanceof PackageDeal ? !!entity.selected : false)).length;

	useEffect(() => (setViewingService(undefined), setViewingPackage(undefined)), [entityType, entities, selected]);

	return (
		<>
			<EntityList
				entities={entities}
				selected={selected}
				onSelectChange={onSelectChange}
				hideSelectAllButton={!hasWritePermission}
				paddingX={paddingX}
				{...props}
			>
				{entities.map((entity, i) => (
					<EntityListItem
						key={`service-package-${entity.id}-${i}`}
						sx={{ paddingRight: paddingX }}
						cardProps={{ sx: { paddingLeft: 4 } }}
						selected={!!selected.find((s) => s.id === entity.id)}
						notSelectable={!hasWritePermission}
						actions={[
							{
								text: <Tr.Portal path="view" />,
								onClick: () => setViewingService(entity as AdditionalService),
								hidden: !(entity instanceof AdditionalService)
							},
							{ text: <Tr.Portal path="edit" />, onClick: () => onEdit?.(entity), hidden: !(onEdit && hasWritePermission) },
							{
								text: <Tr.Portal path="delete" />,
								onClick: () => onDelete?.(entity),
								hidden: !(onDelete && hasWritePermission)
							}
						]}
						onSelectChange={(status) => handleSelectChange(status, entity)}
					>
						<Stack direction="row" spacing={2} sx={{ width: 1.0 }}>
							<PackageDealAndServiceLogoViewer src={entity.logoUrl || ''} />

							<Grid container columnSpacing={2} flexWrap="nowrap">
								{entity instanceof AdditionalService ? (
									<>
										<Grid item xs={3}>
											<EntityListItemCell title={<Tr.Portal path="package-deal.service-name" />}>
												<Typography>{entity.name || '-'}</Typography>
											</EntityListItemCell>
										</Grid>
										<Grid item xs={3}>
											<EntityListItemCell title={<Tr.Portal path="package-deal.price" />}>
												<Typography>{NumberUtils.formatAsPrice(entity.price, { pureDefault: true })}</Typography>
											</EntityListItemCell>
										</Grid>
										<Grid item xs={3}>
											<EntityListItemCell title={<Tr.Portal path="package-deal.type" />}>
												<Typography>{entity.serviceType || '-'}</Typography>
											</EntityListItemCell>
										</Grid>
										<Grid item xs={3}>
											<EntityListItemCell title={<Tr.Portal path="package-deal.supplier" />}>
												<Typography>{entity.supplier || '-'}</Typography>
											</EntityListItemCell>
										</Grid>
									</>
								) : null}
								{entity instanceof PackageDeal ? (
									<>
										<Grid item xs={5}>
											<EntityListItemCell title={<Tr.Portal path="package-deal.package-name" />}>
												<Typography>{entity.name || '-'}</Typography>
											</EntityListItemCell>
										</Grid>
										<Grid item xs={4}>
											<EntityListItemCell title={<Tr.Portal path="package-deal.services-included" />}>
												<Stack direction="row" alignItems="center" spacing={1}>
													<Typography>{Array.isArray(entity.services) ? entity.services.length : '0'}</Typography>
													<IconButton
														size="small"
														sx={{ color: 'common.black' }}
														onClick={() => setViewingPackage(entity)}
													>
														<InfoOutlinedIcon color="inherit" fontSize="small" />
													</IconButton>
												</Stack>
											</EntityListItemCell>
										</Grid>
										<Grid item xs={3}>
											<EntityListItemCell title={<Tr.Portal path="package-deal.selected" />}>
												<Switch
													checked={entity.selected || false}
													onChange={(e) => onPackageDealSelectionChange?.(entity, e.target.checked)}
													disabled={!entity.selected && totalSelectionCount >= MAX_SELECTABLE_PACKAGE_DEAL}
													inputProps={
														{
															name: `package-deal-active-switch-${entity.id}`,
															'data-testid': 'package-deal-active-switch'
														} as any
													}
												/>
											</EntityListItemCell>
										</Grid>
									</>
								) : null}
							</Grid>
						</Stack>
					</EntityListItem>
				))}
			</EntityList>
			<PackageDealServiceDetailDialog open={!!viewingService} entity={viewingService} onClose={() => setViewingService(undefined)} />
			<PackageDealIncludedServicesDialog
				open={!!viewingPackage}
				entity={viewingPackage}
				onClose={() => setViewingPackage(undefined)}
			/>
		</>
	);
};
